import { model as router } from '!/router'
import { model as authorizePaymentModel } from '$/authorize-payment'
import { model as cartModel } from '$/cart'
import { model as creditCardModel } from '$/credit-card'
import { model as creditCardsModel } from '$/credit-cards'
import { F, nay, T } from '@/helpers'
import { addScriptAsync } from '@/tools/script'
import {
  api,
  PaymentEnvEnum,
  PurchasePaymentChannelEnum,
  type ICartProperties,
  type ICreditCard,
} from '@setplex/tria-api'
import {
  createEffect,
  createEvent,
  createStore,
  sample,
  type Effect,
} from 'effector'
import { reset } from 'patronum'
import { model as genericPaymentModel } from '~/features/payment/generic'
import { PaymentStepsAuthorize } from '~/features/payment/interfaces/authorize'

/*
  Authorise flow

  -> user presses buy btn -> popup opened with options - SelectPriceSettings step
    -> app adds item to cart - POST /cart/items
  -> user selects payment option -> Add new card or choose existing one
  -> if user adds new card and submits it -> AddNewCard step
    -> app sends card data to Authorise
    -> app updates cart data PUT cart/properties
    -> app confirms payment POST cart/confirm
  -> if user selects existing card
    -> ?
  -> user presses submit with new or selected card
    -> ?
*/

/*
  Flow cases
   1️⃣ - SelectPriceSettings - generic flow is used
   2️⃣ - SelectCreditCard
   3️⃣ - AddNewCard
   4️⃣ - SuccessPurchaseConfirmation - generic flow is used
 */

export const openAuthorizePopup = createEvent()
export const closeAuthorizePopup = createEvent()

export const selectRememberCardCheckbox = createEvent()

export const selectCreditCard = createEvent<{ creditCard: ICreditCard }>()

export const addNewCreditCard = createEvent()

export const confirmPaymentWithNewCard = createEvent()

export const confirmPaymentWithSavedCard = createEvent() // skin event

export const confirmPaymentWithSavedCardAfterPurchaseFailed = createEvent()

export const confirmPaymentWithNewCardAfterPurchaseFailed = createEvent()

export const $rememberCreditCardValue = createStore<boolean>(false).on(
  selectRememberCardCheckbox,
  (currentValue) => !currentValue
)

export const $selectedCreditCard = createStore<ICreditCard | null>(null)
  .on(selectCreditCard, (_, { creditCard }) => creditCard)
  .on(
    api.creditCards.getSavedCreditCardsFx.doneData,
    (current, { creditCards }) => creditCards?.[0] ?? current
  )

export const loadAuthorizeScriptFx: Effect<
  { isAuthorizeScriptLoaded: boolean; env?: string },
  void
> = createEffect()
loadAuthorizeScriptFx.use(async ({ isAuthorizeScriptLoaded, env }) => {
  if ('Accept' in window && isAuthorizeScriptLoaded) return

  if (env === PaymentEnvEnum.sandbox) {
    // load test
    await addScriptAsync('https://jstest.authorize.net/v1/Accept.js')
  }
  if (env === PaymentEnvEnum.production) {
    // load prod
    await addScriptAsync('https://js.authorize.net/v1/Accept.js')
  }
})

const $isAuthorizeScriptLoaded = createStore<boolean>(false).on(
  loadAuthorizeScriptFx.done,
  T
)

sample({
  clock: genericPaymentModel.openPaymentPopupWithType,
  source: { isAuthorizeScriptLoaded: $isAuthorizeScriptLoaded },
  filter: (_, channel) =>
    // if Authorize is prefered payment system
    channel?.paymentChannel === PurchasePaymentChannelEnum.AUTHORIZE_NET,
  fn: ({ isAuthorizeScriptLoaded }, clockData) => ({
    isAuthorizeScriptLoaded,
    env: clockData?.environment,
  }),
  target: loadAuthorizeScriptFx,
})

sample({
  clock: loadAuthorizeScriptFx.done,
  target: openAuthorizePopup,
})

sample({
  clock: genericPaymentModel.closePaymentPopupWithType,
  filter: (channel) =>
    channel?.paymentChannel === PurchasePaymentChannelEnum.AUTHORIZE_NET,
  target: closeAuthorizePopup,
})

export const $authorizePopupIsOpened = createStore<boolean>(false)
  .on(openAuthorizePopup, T)
  .on(closeAuthorizePopup, F)
  .on(router.$pathname, F)

sample({
  clock: $authorizePopupIsOpened,
  target: genericPaymentModel.$isAnyPaymentPopupOpened,
})

/****** Saved Credit Cards ********/

sample({
  clock: cartModel.postItemToCartFx.done,
  source: {
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
    paymentChannel: genericPaymentModel.$prioritizedPaymentChannel,
  },
  filter: ({ purchaseProcessIsFailed, paymentChannel }) =>
    !purchaseProcessIsFailed &&
    // TODO wrap all generic request with attach to remove that checks
    paymentChannel?.paymentChannel === PurchasePaymentChannelEnum.AUTHORIZE_NET,
  target: api.creditCards.getSavedCreditCardsFx,
})
/****** Saved Credit Cards ********/

/****** Steps Management ********/

export const $currentAuthorizeStep = createStore<
  (typeof PaymentStepsAuthorize)[keyof typeof PaymentStepsAuthorize]
>(PaymentStepsAuthorize.SelectPriceSettings).reset([
  genericPaymentModel.resetStep,
  closeAuthorizePopup,
])

sample({
  clock: api.creditCards.getSavedCreditCardsFx.doneData,
  filter: ({ creditCards }) => creditCards.length > 0,
  fn: () => PaymentStepsAuthorize.SelectCreditCard,
  target: $currentAuthorizeStep,
})

sample({
  clock: api.creditCards.getSavedCreditCardsFx.doneData,
  filter: ({ creditCards }) => creditCards.length < 1,
  fn: () => PaymentStepsAuthorize.AddNewCard,
  target: $currentAuthorizeStep,
})

sample({
  clock: api.cart.confirmCartPurchaseFx.doneData,
  source: { paymentChannel: genericPaymentModel.$prioritizedPaymentChannel },
  filter: ({ paymentChannel }) =>
    paymentChannel?.paymentChannel === PurchasePaymentChannelEnum.AUTHORIZE_NET,
  fn: (_, info) => {
    // paymentChannel is AUTHORIZE_NET means this should exist

    const aNetPayRequest = info.payment.aNetPayRequest!
    return {
      payment: {
        channel: PurchasePaymentChannelEnum.AUTHORIZE_NET,
        method: info.payment.method,
        aNetPayRequest: {
          purchaseNumber: aNetPayRequest.purchaseNumber,
          customerProfileId: aNetPayRequest.customerProfileId,
          paymentProfileId: aNetPayRequest.paymentProfileId,
          dataDescriptor: aNetPayRequest.dataDescriptor,
          dataValue: aNetPayRequest.dataValue,
        },
      },
    }
  },
  target: api.purchases.initializePurchaseProcessFx,
})

sample({
  clock: api.purchases.initializePurchaseProcessFx.done,
  source: { paymentChannel: genericPaymentModel.$prioritizedPaymentChannel },
  filter: ({ paymentChannel }) =>
    paymentChannel?.paymentChannel === PurchasePaymentChannelEnum.AUTHORIZE_NET,
  fn: () => PaymentStepsAuthorize.SuccessPurchaseConfirmation,
  target: $currentAuthorizeStep,
})

sample({
  clock: cartModel.postItemToCartFx.done,
  source: {
    currentPaymentStep: $currentAuthorizeStep,
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
  },
  filter: ({ purchaseProcessIsFailed, currentPaymentStep }) =>
    purchaseProcessIsFailed &&
    currentPaymentStep === PaymentStepsAuthorize.SelectCreditCard,
  target: confirmPaymentWithSavedCardAfterPurchaseFailed,
})

sample({
  clock: cartModel.postItemToCartFx.done,
  source: {
    currentPaymentStep: $currentAuthorizeStep,
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
  },
  filter: ({ purchaseProcessIsFailed, currentPaymentStep }) =>
    purchaseProcessIsFailed &&
    currentPaymentStep === PaymentStepsAuthorize.AddNewCard,
  target: confirmPaymentWithNewCardAfterPurchaseFailed,
})

sample({
  clock: addNewCreditCard,
  fn: () => PaymentStepsAuthorize.AddNewCard,
  target: $currentAuthorizeStep,
})

sample({
  clock: genericPaymentModel.goBackToPreviousStep,
  source: $selectedCreditCard,
  filter: (selectedCreditCard) => Boolean(selectedCreditCard),
  fn: () => PaymentStepsAuthorize.SelectCreditCard,
  target: $currentAuthorizeStep,
})

sample({
  clock: genericPaymentModel.goBackToPreviousStep,
  source: $selectedCreditCard,
  filter: (selectedCreditCard) => nay(selectedCreditCard),
  fn: () => PaymentStepsAuthorize.SelectPriceSettings,
  target: $currentAuthorizeStep,
})
/****** Steps Management ********/

sample({
  clock: confirmPaymentWithNewCard,
  source: {
    creditCardNumber: creditCardModel.$creditCardNumber,
    creditCardMonth: creditCardModel.$creditCardMonth,
    creditCardYear: creditCardModel.$creditCardYear,
    creditCardCvv: creditCardModel.$creditCardCvv,
    creditCardSettings: creditCardsModel.$authorizeCreditCardSettings,
  },
  filter: ({
    creditCardNumber,
    creditCardMonth,
    creditCardYear,
    creditCardCvv,
  }) =>
    Boolean(creditCardNumber) &&
    Boolean(creditCardMonth) &&
    Boolean(creditCardYear) &&
    Boolean(creditCardCvv),
  fn: ({
    creditCardNumber,
    creditCardMonth,
    creditCardYear,
    creditCardCvv,
    creditCardSettings,
  }) => ({
    cardData: {
      cardNumber: creditCardNumber,
      month: creditCardMonth,
      year: creditCardYear,
      cardCode: creditCardCvv,
    },
    authData: {
      clientKey: creditCardSettings?.authorizePublicClientKey || '',
      apiLoginID: creditCardSettings?.apiLogin || '',
    },
  }),
  target: authorizePaymentModel.sendPaymentDataToAuthorizeFx,
})

sample({
  clock: authorizePaymentModel.sendPaymentDataToAuthorizeFx.done,
  source: {
    actualAuthorizeCreditCardData:
      authorizePaymentModel.$actualAuthorizeCreditCardData,
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
    rememberCreditCardValue: $rememberCreditCardValue,
  },
  filter: ({ purchaseProcessIsFailed }) => !purchaseProcessIsFailed,
  fn: ({
    rememberCreditCardValue: saveCreditCard,
    actualAuthorizeCreditCardData,
  }) => {
    const { dataValue, dataDescriptor } =
      actualAuthorizeCreditCardData?.opaqueData || {}

    return {
      payment: {
        cartPaymentChannel: PurchasePaymentChannelEnum.AUTHORIZE_NET as string,
        cartPaymentMethod: 'CREDIT_CARD',
        cartCreditCardData: {
          dataValue,
          dataDescriptor,
          saveCreditCard,
        },
      },
    } as ICartProperties
  },
  target: api.cart.setPaymentDataCartFx,
})

sample({
  clock: confirmPaymentWithNewCardAfterPurchaseFailed,
  source: {
    actualAuthorizeCreditCardData:
      authorizePaymentModel.$actualAuthorizeCreditCardData,
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
    rememberCreditCardValue: $rememberCreditCardValue,
  },
  fn: ({
    rememberCreditCardValue: saveCreditCard,
    actualAuthorizeCreditCardData,
  }) => {
    const { dataValue, dataDescriptor } =
      actualAuthorizeCreditCardData?.opaqueData || {}

    return {
      payment: {
        cartPaymentChannel: PurchasePaymentChannelEnum.AUTHORIZE_NET as string,
        cartPaymentMethod: 'CREDIT_CARD',
        cartCreditCardData: {
          dataValue,
          dataDescriptor,
          saveCreditCard,
        },
      },
    } as ICartProperties
  },
  target: api.cart.setPaymentDataCartFx,
})

sample({
  clock: confirmPaymentWithSavedCard,
  source: {
    selectedCreditCard: $selectedCreditCard,
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
  },
  filter: ({ purchaseProcessIsFailed }) => !purchaseProcessIsFailed,
  fn: ({ selectedCreditCard }) =>
    ({
      payment: {
        cartPaymentChannel: PurchasePaymentChannelEnum.AUTHORIZE_NET as string,
        cartPaymentMethod: 'CREDIT_CARD',
        cartCreditCardData: {
          creditCardId: selectedCreditCard?.id,
        },
      },
    }) as ICartProperties,
  target: api.cart.setPaymentDataCartFx,
})

sample({
  clock: confirmPaymentWithSavedCardAfterPurchaseFailed,
  source: {
    selectedCreditCard: $selectedCreditCard,
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
  },
  fn: ({ selectedCreditCard }) =>
    ({
      payment: {
        cartPaymentChannel: PurchasePaymentChannelEnum.AUTHORIZE_NET as string,
        cartPaymentMethod: 'CREDIT_CARD',
        cartCreditCardData: {
          creditCardId: selectedCreditCard?.id,
        },
      },
    }) as ICartProperties,
  target: api.cart.setPaymentDataCartFx,
})

sample({
  clock: confirmPaymentWithNewCard,
  source: {
    creditCardNumber: creditCardModel.$creditCardNumber,
    creditCardMonth: creditCardModel.$creditCardMonth,
    creditCardYear: creditCardModel.$creditCardYear,
    creditCardCvv: creditCardModel.$creditCardCvv,
    creditCardSettings: creditCardsModel.$authorizeCreditCardSettings,
  },
  filter: ({
    creditCardNumber,
    creditCardMonth,
    creditCardYear,
    creditCardCvv,
  }) =>
    !creditCardNumber || !creditCardMonth || !creditCardYear || !creditCardCvv,
  target: creditCardModel.validateCreditCardFields,
})

sample({
  clock: authorizePaymentModel.sendPaymentDataToAuthorizeFx.pending,
  target: genericPaymentModel.setSpecialRequestIsPending,
})

sample({
  clock: [
    authorizePaymentModel.sendPaymentDataToAuthorizeFx.done,
    confirmPaymentWithSavedCard,
  ],
  source: {
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
  },
  filter: ({ purchaseProcessIsFailed }) => purchaseProcessIsFailed,
  target: cartModel.postItemToCart,
})

sample({
  clock: [
    confirmPaymentWithNewCard,
    confirmPaymentWithSavedCard,
    confirmPaymentWithNewCardAfterPurchaseFailed,
    confirmPaymentWithSavedCardAfterPurchaseFailed,
  ],
  target: genericPaymentModel.resetErrors,
})

sample({
  clock: $currentAuthorizeStep,
  target: genericPaymentModel.resetErrors,
})

reset({
  clock: [closeAuthorizePopup, genericPaymentModel.goBackToPreviousStep],
  target: authorizePaymentModel.$authorizeErrors,
})
